/**
 * use-shares — TanStack Query hooks for the sharing surface.
 *
 * `external-sharing` Phase 3 Session 3.1 (2026-05-09) — `useUnreadShareCount`,
 * `useSentShares`, `useReceivedShares`, `useRevokeShare`.
 * Phase 4 Session 4.1 (2026-05-09) — `useMarkRead`, `useForkShare`.
 *
 * - `useUnreadShareCount` drives `<HeaderShareBell>`'s badge. 60s staleTime;
 *   gated on approved users so unapproved users don't fire pointless requests.
 * - `useSentShares` / `useReceivedShares` back the `/shares` Sent + Received
 *   tabs (Sessions 3.3 + 4.1). 30s staleTime; refetchOnWindowFocus default.
 * - `useRevokeShare` is the owner-side optimistic mutation: pre-flight
 *   cancels in-flight `sent()` queries, snapshots the cache, flips the row's
 *   `status` to `'revoked'` locally, rolls back on error, invalidates on
 *   settled (success OR error) so the server's source-of-truth re-syncs.
 *   Skipped Realtime fan-out: the server's `share_link_revoked` broadcast on
 *   the recipient's `user:<recipientId>:shares` channel handles the
 *   recipient's cache invalidation — owner-side cache stays optimistic-then-
 *   server-confirmed via this mutation alone.
 * - `useMarkRead` is the recipient-side fire-and-forget mutation. The
 *   `<ReceivedTab>` mount-effect calls it for each visible unread row;
 *   `Promise.all` over the batch keeps the UI unblocked. On settled (success
 *   OR error) we invalidate `received()` + `unreadCount()` so the bell badge
 *   re-syncs and the row's `recipientFirstViewedAt` reflects the server.
 * - `useForkShare` creates a new conversation owned by the recipient seeded
 *   with `frozen_state`; on 201, navigates to `/builder?conversation=<newId>
 *   &tab=workbench` and invalidates `conversations.all` + `received()` so
 *   the conversations sidebar shows the fork immediately.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useApprovalStatus } from './use-approval-status';
import { useAuth } from './use-auth';
import { queryKeys } from '@/lib/query-keys';
import { posthog } from '@/lib/posthog';
import {
  forkShare,
  getUnreadCount,
  listShares,
  markRead,
  revokeShare,
  type ForkShareResponse,
  type MarkReadResponse,
  type SharedLinkResponse,
  type UnreadCountResponse,
} from '@/lib/shares-api';

const UNREAD_STALE_MS = 60_000;
const SHARES_LIST_STALE_MS = 30_000;

export function useUnreadShareCount() {
  const { user } = useAuth();
  const { isApproved } = useApprovalStatus();
  const enabled = !!user && isApproved;

  return useQuery<UnreadCountResponse>({
    queryKey: queryKeys.shares.unreadCount(),
    queryFn: getUnreadCount,
    enabled,
    staleTime: UNREAD_STALE_MS,
    // refetchOnWindowFocus removed 2026-05-27 — handled centrally by
    // <FocusResumeCoordinator> to avoid the idle-tab refetch thundering herd.
  });
}

export function useSentShares() {
  const { user } = useAuth();
  const { isApproved } = useApprovalStatus();
  const enabled = !!user && isApproved;

  return useQuery<SharedLinkResponse[]>({
    queryKey: queryKeys.shares.sent(),
    queryFn: () => listShares('sent'),
    enabled,
    staleTime: SHARES_LIST_STALE_MS,
  });
}

export function useReceivedShares() {
  const { user } = useAuth();
  const { isApproved } = useApprovalStatus();
  const enabled = !!user && isApproved;

  return useQuery<SharedLinkResponse[]>({
    queryKey: queryKeys.shares.received(),
    queryFn: () => listShares('received'),
    enabled,
    staleTime: SHARES_LIST_STALE_MS,
  });
}

interface RevokeContext {
  prev: SharedLinkResponse[] | undefined;
}

export function useRevokeShare() {
  const queryClient = useQueryClient();

  return useMutation<
    { ok: true; alreadyRevoked: boolean },
    Error,
    string,
    RevokeContext
  >({
    mutationFn: (shareId: string) => revokeShare(shareId),
    onMutate: async (shareId) => {
      // Cancel any in-flight `sent()` refetches so they don't overwrite our
      // optimistic update on resolve.
      await queryClient.cancelQueries({ queryKey: queryKeys.shares.sent() });
      const prev = queryClient.getQueryData<SharedLinkResponse[]>(
        queryKeys.shares.sent()
      );
      if (prev) {
        const now = new Date().toISOString();
        queryClient.setQueryData<SharedLinkResponse[]>(
          queryKeys.shares.sent(),
          prev.map((s) =>
            s.id === shareId
              ? { ...s, status: 'revoked' as const, revokedAt: now }
              : s
          )
        );
      }
      return { prev };
    },
    onSuccess: (_data, shareId) => {
      // NEW Phase 3 § 3.4 — PostHog product event: share revoked.
      posthog.capture('share_revoked', { share_id: shareId });
    },
    onError: (_err, _shareId, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(queryKeys.shares.sent(), ctx.prev);
      }
    },
    onSettled: () => {
      // Re-sync against server source-of-truth regardless of outcome.
      void queryClient.invalidateQueries({ queryKey: queryKeys.shares.sent() });
    },
  });
}

/**
 * Mark a received share as read on first view. Idempotent server-side — the
 * `recipient_first_viewed_at` stamp only writes if NULL. The `<ReceivedTab>`
 * mount-effect fires this for each visible unread row in parallel; we
 * invalidate the recipient-facing caches on settled so the bell badge clears
 * and the row's `recipientFirstViewedAt` reflects the server.
 *
 * Not optimistic: the server is authoritative for `recipient_first_viewed_at`,
 * and the [NEW] chip fading on the next render (~200ms) is acceptable UX —
 * the chip dissolution is itself a "we saw it" signal.
 */
export function useMarkRead() {
  const queryClient = useQueryClient();

  return useMutation<MarkReadResponse, Error, string>({
    mutationFn: (shareId: string) => markRead(shareId),
    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.shares.received(),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.shares.unreadCount(),
      });
    },
  });
}

/**
 * Fork an active share into a new conversation owned by the recipient. The
 * server seeds `agent_state.inputValues` from the share's `frozen_state` and
 * stamps `forked_from_share_id` + `forked_from_conversation_id` on the new
 * conversation. On 201, navigate to the new Builder + invalidate the
 * conversations + received caches so the sidebar surfaces the fork and the
 * Received tab re-syncs (the row stays — fork doesn't revoke the share).
 */
export function useForkShare() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation<ForkShareResponse, Error, string>({
    mutationFn: (shareId: string) => forkShare(shareId),
    onSuccess: (res, shareId) => {
      // NEW Phase 3 § 3.4 — PostHog product event: share forked.
      posthog.capture('share_forked', { share_id: shareId });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.conversations.all,
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.shares.received(),
      });
      void navigate({
        to: '/builder',
        search: { conversation: res.conversationId, tab: 'workbench' as const },
      });
    },
    onError: (err, shareId) => {
      // Without this, fork failures (401/403/404/500) silently no-op — the
      // user clicks Fork and sees nothing happen because nothing in the
      // UI reads `mutation.isError`. Log the structured error so the
      // failure is at least visible in DevTools, and so the consuming
      // component can read `mutation.error` and render a banner.
      // eslint-disable-next-line no-console
      console.error('[useForkShare] fork failed', { shareId, error: err });
    },
  });
}
